import * as React from 'react';
import { Formik, FormikHelpers } from 'formik';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { EmployeeOffboardingAppointmentHandling } from '@prisma/client';
import { ROUTES } from '../../../constants/global';
import { offboardingWizardInitialValues } from '../../../constants/offboardings';
import {
  buildOffboardingPayload,
  offboardingToWizardValues,
  offboardingWizardSchema,
} from '../../../helpers/offboardings';
import {
  CreateOffboardingPayload,
  Offboarding,
  OffboardingWizardValues,
  UpdateOffboardingPayload,
} from '../../../types/offboardings';
import useCreateOffboarding from '../../../hooks/offboardings/useCreateOffboarding';
import useUpdateOffboarding from '../../../hooks/offboardings/useUpdateOffboarding';
import useUser from '../../../hooks/global/useUser';
import useLoadingModal from '../../../hooks/global/useLoadingModal';
import useFeatureGuard from '../../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../../types/subscriptions';
import { LoadedUser } from '../../../interfaces';
import WizardShell from './WizardShell';

interface Props {
  businessId: string;
  employeeId: string;
  employeeName: string;
  isSelf: boolean;
  existingOffboarding?: Offboarding | null;
}

function OffboardingWizard({
  businessId,
  employeeId,
  employeeName,
  isSelf,
  existingOffboarding,
}: Props) {
  const navigate = useNavigate();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { triggerReload } = useUser<LoadedUser>();
  const hasMarketingAccess = useFeatureGuard(Feature.MARKETING);

  const initialValues = React.useMemo(
    () =>
      existingOffboarding
        ? offboardingToWizardValues(existingOffboarding)
        : offboardingWizardInitialValues,
    [existingOffboarding],
  );

  const onMutationError = React.useCallback((error: unknown) => {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? 'Failed to save offboarding';
    toast.error(message);
  }, []);

  const onLoadingOpen = React.useCallback(
    () =>
      openLoadingModal({
        header: existingOffboarding
          ? 'Updating offboarding'
          : 'Starting offboarding',
        message: 'Please wait…',
      }),
    [existingOffboarding, openLoadingModal],
  );

  const create = useCreateOffboarding({
    onMutate: onLoadingOpen,
    onSettled: closeLoadingModal,
    onError: onMutationError,
  });
  const update = useUpdateOffboarding({
    onMutate: onLoadingOpen,
    onSettled: closeLoadingModal,
    onError: onMutationError,
  });

  const onSubmit = async (
    values: OffboardingWizardValues,
    helpers: FormikHelpers<OffboardingWizardValues>,
  ) => {
    const base = buildOffboardingPayload(values);

    let created: Offboarding | null = null;
    if (existingOffboarding) {
      const payload: UpdateOffboardingPayload = base;
      await update.mutateAsync({
        offboardingId: existingOffboarding.id,
        payload,
      });
    } else {
      const payload: CreateOffboardingPayload = {
        businessId,
        employeeId,
        ...base,
      };
      created = await create.mutateAsync(payload);
    }

    // Local dev runs the GCP task synchronously, so the artist's ETB row may
    // already be gone by now. Reload useUser to drop them from the team list.
    triggerReload();
    toast.success(
      existingOffboarding ? 'Offboarding updated' : 'Offboarding initiated',
    );
    // Clear dirty so the exit-confirm guard won't fire in the brief window
    // before navigate(/team/) unmounts the wizard.
    helpers.resetForm({ values });

    // For STAY offboardings with marketing access, hand the campaign prompt
    // off via router state so it overlays the team page after the wizard
    // unmounts — see OffboardingCampaignPromptHandler.
    const qualifiesForCampaignPrompt =
      created &&
      hasMarketingAccess &&
      created.appointmentHandling ===
        EmployeeOffboardingAppointmentHandling.STAY_AT_SHOP;

    navigate(
      ROUTES.team,
      qualifiesForCampaignPrompt
        ? {
            state: {
              offboardingCampaignPrompt: {
                offboardingId: created!.id,
                employeeName,
              },
            },
          }
        : undefined,
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={offboardingWizardSchema}
      onSubmit={onSubmit}
      enableReinitialize>
      <WizardShell
        employeeName={employeeName}
        isEdit={!!existingOffboarding}
        isSelf={isSelf}
      />
    </Formik>
  );
}

export default OffboardingWizard;
